import isEqual from 'lodash/isEqual';
import { useEffect, useRef } from 'react';

import { DataProviderQueryStatus } from '../../next/CustomDash/components/DataProvider/types';
import type { RevelioPageId } from '../../next/CustomDash/components/PageConfigProvider/types';
import { useFilterContext } from '../../next/CustomDash/filters/contexts/FilterProvider';
import {
  FilterId,
  type FilterSelectionsMap,
} from '../../next/CustomDash/filters/types/filters';
import { isScreenerV2Page } from '../../next/CustomDash/utils/routing-helpers';
import { useActivityTaxonomyConfig } from '../selection-lists/hooks/useActivityTaxonomyConfig';
import {
  type PreferenceScope,
  UserPreferenceControlledFilterIds,
  UserPreferenceKey,
  type UserPreferences,
} from './types';
import { UserPreferencesDataProvider } from './UserPreferencesDataProvider';
import { deriveEffectiveTaxonomiesForScope } from './utils/derive-effective-taxonomies';
import {
  filtersToPreferences,
  preferenceAdapters,
} from './utils/preference-filter-adapters';
import {
  getPreferenceScope,
  updateCustomDashFilters,
} from './utils/preferences';
import { resolveWorkforceSegment } from './utils/workforce-segment';

const isPreferenceUserSubmitted = (
  key: UserPreferenceKey,
  activeFilterMap: FilterSelectionsMap
): boolean => {
  const adapter = preferenceAdapters[key];
  if (!adapter?.relevantFilters) return false;

  // Only check filters that are UI preference controlled
  const controlledFilters = adapter.relevantFilters.filter((fid) =>
    UserPreferenceControlledFilterIds.includes(fid as unknown as string)
  );

  // If any controlled filter is user-submitted, skip this preference.
  // NOTE: this effect-level check reads a render-closure `activeFilterMap`
  // that can be stale relative to a just-dispatched AI apply — protecting
  // AI-paired (`isAiApplied`) filters therefore does NOT happen here but in
  // the reducer, which skips `source: 'preference-sync'` writes over live
  // `isAiApplied` state (see filterReducer's UPDATE_FILTER guard).
  return controlledFilters.some(
    (fid) => activeFilterMap.get(fid)?.isUserSubmitted
  );
};

/**
 * Per-subkey variant of the user-submitted gate for the Taxonomies key: a
 * user-submitted taxonomy filter must block only ITS OWN preference sync.
 * The whole-key gate would let a user-submitted skill (or activity) taxonomy
 * silently stop the role preference syncing too.
 */
const withoutUserSubmittedTaxonomies = (
  taxonomies: UserPreferences[UserPreferenceKey.Taxonomies] | undefined,
  activeFilterMap: FilterSelectionsMap
): UserPreferences[UserPreferenceKey.Taxonomies] | undefined => {
  if (!taxonomies) return taxonomies;
  const next = { ...taxonomies };
  if (activeFilterMap.get(FilterId.ROLE_TAXONOMY)?.isUserSubmitted) {
    delete next.role;
    delete next.effectiveRoleTaxonomySelection;
  }
  if (activeFilterMap.get(FilterId.SKILL_TAXONOMY)?.isUserSubmitted) {
    delete next.skill;
    delete next.effectiveSkillTaxonomySelection;
  }
  if (activeFilterMap.get(FilterId.ACTIVITY_TAXONOMY)?.isUserSubmitted) {
    delete next.activity;
    delete next.effectiveActivityTaxonomySelection;
  }
  return next;
};

const EffectRunner = ({
  preferences,
  activeFilterMap,
  affectedScope,
  revelioPageId,
  skipTaxonomies,
}: {
  status: DataProviderQueryStatus;
  preferences: UserPreferences;
  activeFilterMap: FilterSelectionsMap;
  affectedScope: PreferenceScope;
  revelioPageId?: RevelioPageId;
  skipTaxonomies?: boolean;
}) => {
  const { dispatch } = useFilterContext();
  const { activityTaxonomyEnabled } = useActivityTaxonomyConfig();
  const lastAffectedScope = useRef<PreferenceScope>();

  useEffect(() => {
    if (lastAffectedScope.current === affectedScope) {
      return;
    }

    // Filter out preferences that have been populated from deeplinks (potentially from deeplink sharer's preferences)
    const filteredPreferences = Object.entries(preferences).reduce(
      (acc, [key, value]) => {
        const prefKey = key as UserPreferenceKey;
        if (prefKey === UserPreferenceKey.Currency) {
          return acc;
        }
        // Screener v2 pages should not apply taxonomy preferences - always use
        // default. Callers can also opt out entirely via `skipTaxonomies` when
        // their taxonomy selection must come from another source (e.g. the AI
        // assistant's isolated chart preview scope, whose taxonomy is paired
        // with the AI response's taxonomy-relative ids).
        if (
          prefKey === UserPreferenceKey.Taxonomies &&
          (skipTaxonomies || isScreenerV2Page(revelioPageId))
        ) {
          return acc;
        }
        if (prefKey === UserPreferenceKey.Taxonomies) {
          // Adaptive is only resolvable on COMPANY pages — everywhere else the
          // global choice materializes as the Generic taxonomy.
          const syncableTaxonomies = withoutUserSubmittedTaxonomies(
            deriveEffectiveTaxonomiesForScope(
              value as UserPreferences[UserPreferenceKey.Taxonomies],
              affectedScope
            ),
            activeFilterMap
          );
          // The activity default exists for every user, but the ACTIVITY_TAXONOMY
          // filter must not: syncing it would re-insert the filter into a map
          // createDefaultFilterMap deliberately gated it out of, and from there
          // it leaks into every flag-off user's URL serialization.
          if (syncableTaxonomies && activityTaxonomyEnabled !== true) {
            delete syncableTaxonomies.activity;
            delete syncableTaxonomies.effectiveActivityTaxonomySelection;
          }
          const hasSyncableSubkey =
            syncableTaxonomies !== undefined &&
            (syncableTaxonomies.role !== undefined ||
              syncableTaxonomies.skill !== undefined ||
              syncableTaxonomies.activity !== undefined);
          return hasSyncableSubkey
            ? { ...acc, [prefKey]: syncableTaxonomies }
            : acc;
        }
        const isUserSub = isPreferenceUserSubmitted(prefKey, activeFilterMap);
        if (!isUserSub) {
          return { ...acc, [prefKey]: value };
        }
        return acc;
      },
      {} as Partial<UserPreferences>
    );

    if (Object.keys(filteredPreferences).length > 0) {
      // Only update filters if the values are actually different from current state
      const currentPreferences = filtersToPreferences({
        activeFilterMap,
        affectedScope,
      });
      const hasChanges = preferencesHaveChanges({
        filterPreferences: filteredPreferences,
        preferences: currentPreferences,
      });

      if (hasChanges) {
        // `'preference-sync'` lets the reducer skip any write that would
        // clobber an `isAiApplied` filter — the authoritative, live-state
        // guard (this effect's render-closure `activeFilterMap` can be stale
        // relative to a just-dispatched AI apply, so the guard cannot live
        // here).
        updateCustomDashFilters(
          dispatch,
          filteredPreferences,
          undefined,
          'preference-sync'
        );
      }
      lastAffectedScope.current = affectedScope;
    }
  }, [
    preferences,
    dispatch,
    activeFilterMap,
    affectedScope,
    revelioPageId,
    skipTaxonomies,
    activityTaxonomyEnabled,
  ]);
  return null;
};

const preferencesHaveChanges = ({
  filterPreferences,
  preferences,
}: {
  filterPreferences: Partial<UserPreferences>;
  preferences: Partial<UserPreferences>;
}) => {
  const hasChanges = Object.keys(filterPreferences).some((key) => {
    const prefKey = key as UserPreferenceKey;
    if (prefKey === UserPreferenceKey.ContingentWorkers) {
      if (preferences[prefKey] === undefined) {
        return true;
      }
      return (
        resolveWorkforceSegment(filterPreferences[prefKey]) !==
        resolveWorkforceSegment(preferences[prefKey])
      );
    }
    if (prefKey === UserPreferenceKey.DataAdjustments) {
      if (
        filterPreferences[prefKey] === false &&
        preferences[prefKey] === false
      ) {
        return false;
      }
      return !isEqual(filterPreferences[prefKey], preferences[prefKey]);
    }
    return !isEqual(filterPreferences[prefKey], preferences[prefKey]);
  });
  return hasChanges;
};

/**
 * Syncs the initial preferences to the filters when the affected scope changes. This is
 * for the case where people share urls with people who have different preferences. In
 * those cases, we want the preferences to show up the same as the user sharing the url.
 */
export const SyncInitialPreferencesToFilters = ({
  revelioPageId,
  skipTaxonomies,
}: {
  revelioPageId?: RevelioPageId;
  /**
   * Never sync the `taxonomies` preference into this filter scope. For scopes
   * whose taxonomy selection is owned by another mechanism (the AI assistant's
   * isolated chart preview pairs it with the response's taxonomy-relative ids);
   * all other preferences still sync as usual.
   */
  skipTaxonomies?: boolean;
}) => {
  const { filterState, activeFilterMap } = useFilterContext();
  const affectedScope = getPreferenceScope({
    pageId: revelioPageId,
    primaryFilterId: filterState.activeKey ?? 'NO_PRIMARY_FILTER',
  });

  return (
    <UserPreferencesDataProvider
      key={affectedScope}
      affectedScope={affectedScope}
    >
      {({ data, status }) => {
        if (!data || status !== DataProviderQueryStatus.Success) {
          return null;
        }
        return (
          <EffectRunner
            key={affectedScope}
            status={status}
            preferences={data.preferences}
            activeFilterMap={activeFilterMap}
            affectedScope={affectedScope}
            revelioPageId={revelioPageId}
            skipTaxonomies={skipTaxonomies}
          />
        );
      }}
    </UserPreferencesDataProvider>
  );
};
